import moment from 'moment';
import { PALETTES } from '../shared/ColourPicker';
import { ONBOARDING_DEMO_VISIBLE } from '../../config/featureFlags';

/**
 * Diary demo data (staging only).
 *
 * Fills the diary with a believable day for a chosen trade, so the app can be
 * shown, screenshotted and filmed without a customer's real book. Nothing is
 * written anywhere: the generator returns the same shape the diary API returns
 * and DiaryV2 renders it instead of calling the API. Switching the demo off
 * brings the real diary straight back.
 *
 * Colours come from the same palettes as the service colour picker, so a demo
 * looks like a salon that has themed its diary.
 */

const KEY = 'beu:diary-demo';
const WALKIN_CLIENT_ID = '64f5c7789a069cda0188fea3';

/* ---------------- trades ---------------- */

export const TRADES = {
  salon: {
    label: 'Hair salon',
    blurb: 'Colour, cuts and blow dries across four chairs',
    staff: [
      { name: 'Sue Perm', start: 9 * 60, end: 17 * 60, owner: true },
      { name: 'Bobbi Trim', start: 9 * 60, end: 18 * 60 },
      { name: 'Ella Gance', start: 9 * 60, end: 17 * 60 + 30 },
      { name: 'Marco Waves', start: 10 * 60, end: 18 * 60 },
    ],
    services: [
      { name: 'Cut and Finish', time: 60, price: 48, weight: 5 },
      { name: 'Blow Dry', time: 45, price: 30, weight: 4 },
      { name: 'Gents Cut', time: 30, price: 22, weight: 3 },
      { name: 'Root Tint', time: 90, price: 65, weight: 3 },
      { name: 'Half Head Highlights', time: 120, price: 85, weight: 2 },
      { name: 'Full Head Colour', time: 120, price: 75, weight: 2 },
      { name: 'Balayage', time: 180, price: 130, weight: 1 },
      { name: 'Toner', time: 30, price: 25, weight: 2 },
      { name: 'Conditioning Treatment', time: 30, price: 20, weight: 2 },
    ],
    pairs: [['Root Tint', 'Cut and Finish'], ['Half Head Highlights', 'Toner'], ['Full Head Colour', 'Blow Dry']],
    breaks: [{ title: 'Lunch', emoji: '🥪', minutes: 45 }, { title: 'Coffee break', emoji: '☕', minutes: 15 }],
    clients: ['Anita Curl', 'Barb Dwyer', 'Carrie Oakey', 'Crystal Waters', 'Daisy Chain', 'Dee Frizz', 'Ella Vator', 'Fay Slift', 'Gail Force', 'Ginger Snapp', 'Hazel Nutt', 'Holly Wood', 'Ivy Greene', 'Lacey Curls', 'Lily Pond', 'Mary Gold', 'Paige Turner', 'Pearl Divine', 'Polly Ester', 'Rose Bush', 'Sally Forth', 'Sandy Beech', 'Sue Flay', 'Tess Tube', 'Wanda Lust', 'Bunny Hopp', 'Anna Gram', 'Sherry Trifle', 'Bea Keeper', 'Maggie Pie', 'Penny Farthing', 'Tia Maria', 'Isabelle Ringing', 'Robyn Banks', 'Will Barrow', 'Dan Druff', 'Rick O Shea', 'Barry Cade', 'Justin Case', 'Bill Board', 'Al Fresco', 'Neil Down', 'Ray Gunn', 'Mark Mywords', 'Chris P Bacon', 'Sonny Day'],
  },
  barber: {
    label: 'Barbershop',
    blurb: 'Fades, beards and walk-ins, back to back',
    staff: [
      { name: 'Mo Hawk', start: 9 * 60, end: 18 * 60, owner: true },
      { name: 'Sid Burns', start: 9 * 60, end: 17 * 60 },
      { name: 'Barry Blades', start: 10 * 60, end: 19 * 60 },
      { name: 'Buzz Cutt', start: 9 * 60 + 30, end: 18 * 60 + 30 },
    ],
    services: [
      { name: 'Skin Fade', time: 30, price: 22, weight: 6 },
      { name: 'Scissor Cut', time: 30, price: 20, weight: 5 },
      { name: 'Cut and Beard', time: 45, price: 30, weight: 4 },
      { name: 'Beard Trim', time: 15, price: 12, weight: 3 },
      { name: 'Kids Cut', time: 20, price: 14, weight: 2 },
      { name: 'Hot Towel Shave', time: 30, price: 20, weight: 2 },
      { name: 'Buzz Cut', time: 15, price: 12, weight: 2 },
      { name: 'Fade and Design', time: 45, price: 28, weight: 1 },
    ],
    pairs: [['Skin Fade', 'Beard Trim'], ['Scissor Cut', 'Beard Trim']],
    breaks: [{ title: 'Lunch', emoji: '🥪', minutes: 30 }, { title: 'Brew break', emoji: '☕', minutes: 15 }],
    clients: ['Will Barrow', 'Dan Druff', 'Rick O Shea', 'Barry Cade', 'Justin Case', 'Bill Board', 'Al Fresco', 'Neil Down', 'Ray Gunn', 'Gene Poole', 'Jim Nasium', 'Pete Moss', 'Sonny Day', 'Robyn Banks', 'Des Troy', 'Doug Graves', 'Frank Lee', 'Chip Munk', 'Tom Foolery', 'Mark Mywords', 'Ivor Beard', 'Harry Legge', 'Stan Still', 'Art Decko', 'Chris P Bacon', 'Ed Itorial', 'Gus Tywind', 'Herb Alteer', 'Ken Tucky', 'Lee Nonmi', 'Max Power', 'Nick O Time', 'Otto Pilot', 'Phil Errup', 'Reg Ister', 'Hugh Morris', 'Tim Burr', 'Vic Tree', 'Walter Melon', 'Barb Dwyer', 'Carrie Oakey'],
    walkInChance: 0.16,
  },
  beauty: {
    label: 'Beauty and nails',
    blurb: 'Brows, lashes, waxing and nails',
    staff: [
      { name: 'Mandy Cure', start: 9 * 60 + 30, end: 18 * 60, owner: true },
      { name: 'Lash Gordon', start: 9 * 60, end: 17 * 60 + 30 },
      { name: 'Bella Buff', start: 10 * 60, end: 19 * 60 },
    ],
    services: [
      { name: 'Brow Wax', time: 15, price: 10, weight: 4 },
      { name: 'Brow Lamination', time: 45, price: 40, weight: 3 },
      { name: 'Lash Lift and Tint', time: 60, price: 45, weight: 3 },
      { name: 'Classic Lash Set', time: 90, price: 55, weight: 2 },
      { name: 'Lash Infills', time: 60, price: 38, weight: 3 },
      { name: 'Gel Manicure', time: 45, price: 30, weight: 4 },
      { name: 'Gel Pedicure', time: 60, price: 35, weight: 2 },
      { name: 'Full Leg Wax', time: 45, price: 30, weight: 2 },
      { name: 'Facial', time: 60, price: 50, weight: 2 },
    ],
    pairs: [['Brow Wax', 'Lash Lift and Tint'], ['Gel Manicure', 'Gel Pedicure']],
    breaks: [{ title: 'Lunch', emoji: '🥪', minutes: 45 }, { title: 'Coffee break', emoji: '☕', minutes: 15 }],
    // Rooms show the resource lanes: treatments that need one are booked into it.
    rooms: [
      { name: 'Treatment Room 1', diaryColour: '#C8B6E2' },
      { name: 'Treatment Room 2', diaryColour: '#B6D8E2' },
    ],
    roomServices: ['Classic Lash Set', 'Lash Infills', 'Lash Lift and Tint', 'Facial', 'Full Leg Wax'],
    clients: ['Rosie Cheeks', 'Anita Wax', 'Pearl Divine', 'Gemma Stone', 'Bella Donna', 'Crystal Clear', 'Faye Shull', 'Hazel Eyes', 'Lacey Lash', 'Lily Whyte', 'Marie Gold', 'Olive Branch', 'Polly Ester', 'Ruby Reds', 'Sandy Shores', 'Summer Breeze', 'Vi Olette', 'Dee Tox', 'Aura Glow', 'Brooke Lynn', 'Ginger Snapp', 'Jas Mine', 'Elle Bow', 'Ivy Vine', 'Nell Polish', 'Sue Flay', 'Barb Dwyer', 'Paige Turner', 'Carrie Oakey', 'Daisy Chain', 'Bea Keeper', 'Cher Bourne', 'Fleur De Lys', 'Glow Rhia', 'Honey Combe', 'Iris Bloom', 'Jade Stone', 'Anne Teak', 'Lotta Glam', 'Claire Voyant', 'Penny Lane', 'Tia Maria', 'Will Barrow', 'Justin Case'],
  },
};

export const TRADE_IDS = ['salon', 'barber', 'beauty'];
export const PALETTE_NAMES = PALETTES.map((p) => p.name);

/* ---------------- settings ---------------- */

const DEFAULTS = { on: false, trade: 'salon', palette: 'Coastal', density: 'steady' };

export function getDiaryDemo() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    return { ...DEFAULTS };
  }
}

export function setDiaryDemo(patch) {
  const next = { ...getDiaryDemo(), ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
  try { window.dispatchEvent(new CustomEvent('beu:diary-demo-changed', { detail: next })); } catch (e) { /* ignore */ }
  return next;
}

export function clearDiaryDemo() {
  try { localStorage.removeItem(KEY); } catch (e) { /* storage unavailable */ }
  try { window.dispatchEvent(new CustomEvent('beu:diary-demo-changed', { detail: { ...DEFAULTS } })); } catch (e) { /* ignore */ }
}

// Belt and braces: the settings page is not registered on customer builds,
// and this makes sure a setting left in a browser can never fill a real
// salon's diary with made-up bookings on prod, beta or rush.
export const DIARY_DEMO_ALLOWED = ONBOARDING_DEMO_VISIBLE;
export const isDiaryDemoOn = () => DIARY_DEMO_ALLOWED && getDiaryDemo().on === true;

/* ---------------- deterministic randomness ---------------- */

// The same day always generates the same diary, so scrolling away and back
// does not reshuffle the bookings mid demo.
function seedFrom(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i += 1) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

const pick = (rnd, arr) => arr[Math.floor(rnd() * arr.length) % arr.length];

function pickWeighted(rnd, services) {
  const total = services.reduce((n, s) => n + (s.weight || 1), 0);
  let r = rnd() * total;
  for (const s of services) {
    r -= (s.weight || 1);
    if (r <= 0) return s;
  }
  return services[services.length - 1];
}

/* ---------------- colours ---------------- */

function paletteColours(name) {
  const p = PALETTES.find((x) => x.name === name) || PALETTES[0];
  return p.colours.map((c) => c.hex);
}

// One colour per service, so the same service is always the same colour on
// the wall planner, the way a salon that themes its diary would set it up.
// Palettes run from pale to near black, and a wall of dark blocks reads as a
// mess, so the six lightest are used and the busiest service gets the palest.
const luminance = (hex) => {
  const h = String(hex).replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

function serviceColours(trade, paletteName) {
  const usable = paletteColours(paletteName)
    .map((hex) => ({ hex, l: luminance(hex) }))
    .sort((a, b) => b.l - a.l)
    .slice(0, 6)
    .map((x) => x.hex);
  const map = {};
  TRADES[trade].services
    .map((s) => s)
    .sort((a, b) => (b.weight || 1) - (a.weight || 1))
    .forEach((s, rank) => { map[s.name] = usable[rank % usable.length]; });
  return map;
}

/** The same service colours the demo diary uses, for other practice screens. */
export const demoServiceColours = (trade, palette) => serviceColours(trade in TRADES ? trade : 'salon', palette);

/* ---------------- rooms ---------------- */

const ROOM_GROUP_ID = 'demo-room-group';

/** Rooms for the trade, in the shape the diary's resource lanes expect. */
export function demoResources(tradeOverride) {
  const trade = tradeOverride || getDiaryDemo().trade;
  const cfg = TRADES[trade] || TRADES.salon;
  if (!cfg.rooms) return { units: [], groups: [] };
  const open = { open: true, start: 9 * 60, end: 19 * 60 };
  const hours = { mode: 'custom', days: { mon: open, tue: open, wed: open, thu: open, fri: open, sat: open, sun: { open: false } } };
  return {
    groups: [{ _id: ROOM_GROUP_ID, name: 'Rooms' }],
    units: cfg.rooms.map((r, i) => ({
      _id: idFor(['room', i]),
      name: r.name,
      groupId: ROOM_GROUP_ID,
      diaryColour: r.diaryColour,
      isActive: true,
      turnoverTime: 10,
      hours,
      images: [],
    })),
  };
}

/* ---------------- the day ---------------- */

const idFor = (parts) => `demo-${parts.join('-')}`;

function dayForStaff({ staff, staffIndex, date, trade, colours, density, usedNames, rooms, roomBusy }) {
  const cfg = TRADES[trade];
  const rnd = seedFrom(`${date}|${trade}|${staffIndex}`);
  const dow = moment(date, 'YYYY-MM-DD').isoWeekday();
  const isToday = date === moment().format('YYYY-MM-DD');
  const nowMinutes = moment().hours() * 60 + moment().minutes();

  // Sunday closed, Monday quieter, weekend busier: a normal salon week.
  if (dow === 7) return { isWorking: false, rota: null, bookings: [] };
  const offStaff = dow === 1 && staffIndex === cfg.staff.length - 1; // someone takes Monday off
  if (offStaff) return { isWorking: false, rota: null, bookings: [] };

  const start = staff.start;
  const end = staff.end;
  const bookings = [];
  let n = 0;

  // Breaks are real blocks on the diary, not empty space: lunch for everyone,
  // and a coffee in the afternoon for most people.
  const breakDefs = cfg.breaks || [];
  const lunchDef = breakDefs[0] || { title: 'Lunch', emoji: '🥪', minutes: 45 };
  const coffeeDef = breakDefs[1];
  const lunchAt = 12 * 60 + Math.floor(rnd() * 3) * 30 + (staffIndex % 2) * 15;
  const lunchLength = lunchDef.minutes;
  const wantsCoffee = !!coffeeDef && rnd() < 0.7;
  const coffeeAt = 15 * 60 + Math.floor(rnd() * 3) * 30;
  const coffeeLength = coffeeDef ? coffeeDef.minutes : 0;
  const events = [
    { _id: idFor([date, staffIndex, 'lunch']), startTime: lunchAt, endTime: lunchAt + lunchLength, title: lunchDef.title, emoji: lunchDef.emoji, diaryColour: '#EFEFF2' },
  ];
  if (wantsCoffee && coffeeAt + coffeeLength < end) {
    events.push({ _id: idFor([date, staffIndex, 'coffee']), startTime: coffeeAt, endTime: coffeeAt + coffeeLength, title: coffeeDef.title, emoji: coffeeDef.emoji, diaryColour: '#EFEFF2' });
  }

  // The day is the shift minus the breaks. Each stretch is filled in turn, and
  // only a service that actually fits the stretch is offered, so "Rammed"
  // leaves no dead half hours and the last client finishes near closing.
  const busy = density === 'busy';
  const gapChance = busy ? 0.07 : 0.62;
  const maxGapSteps = busy ? 1 : 4; // 15 minute steps, so up to an hour free
  const shortest = Math.min(...cfg.services.map((s) => s.time));

  const stretches = [];
  let edge = start;
  events.slice().sort((a, b) => a.startTime - b.startTime).forEach((ev) => {
    if (ev.startTime > edge) stretches.push([edge, ev.startTime]);
    edge = Math.max(edge, ev.endTime);
  });
  if (edge < end) stretches.push([edge, end]);

  stretches.forEach(([from, until]) => {
    let cursor = from;
    while (until - cursor >= shortest && n < 30) {
      const room = until - cursor;

      // Pick from what fits. A pair only when both halves fit the stretch.
      let chosen = null;
      if (rnd() < 0.18 && cfg.pairs.length) {
        const pair = pick(rnd, cfg.pairs);
        const first = cfg.services.find((sv) => sv.name === pair[0]);
        const second = cfg.services.find((sv) => sv.name === pair[1]);
        if (first && second && first.time + second.time <= room) chosen = [first, second];
      }
      if (!chosen) {
        const fits = cfg.services.filter((sv) => sv.time <= room);
        if (!fits.length) break;
        chosen = [pickWeighted(rnd, fits)];
      }
      const length = chosen.reduce((sum, sv) => sum + sv.time, 0);

      // Treatments that need a room take the first one free at that time.
      const needsRoom = (cfg.roomServices || []).some((name) => chosen.some((c) => c.name === name));
      let bookedRoom = null;
      if (needsRoom && rooms && rooms.length) {
        bookedRoom = rooms.find((r) => !(roomBusy[r._id] || []).some(([a, b]) => cursor < b && cursor + length > a)) || null;
        if (bookedRoom) {
          roomBusy[bookedRoom._id] = (roomBusy[bookedRoom._id] || []).concat([[cursor, cursor + length + (bookedRoom.turnoverTime || 0)]]);
        }
      }

      // A client only appears once a day, the way a real book reads. Walk-ins
      // are the exception: a barbershop has several.
      const isWalkIn = rnd() < (cfg.walkInChance || 0);
      let clientName = 'Walk-in';
      if (!isWalkIn) {
        const free = cfg.clients.filter((c) => !usedNames.has(c));
        clientName = free.length ? pick(rnd, free) : pick(rnd, cfg.clients);
        usedNames.add(clientName);
      }
      const done = isToday ? cursor + length <= nowMinutes : moment(date, 'YYYY-MM-DD').isBefore(moment(), 'day');

      bookings.push({
        _id: idFor([date, staffIndex, n]),
        bookingSlot: [cursor, cursor + length],
        bookingStatus: done ? 4 : 1,
        isOnlineBooking: rnd() < 0.45 ? 1 : 0,
        clientId: isWalkIn ? WALKIN_CLIENT_ID : idFor([date, staffIndex, n, 'client']),
        user: isWalkIn ? null : { _id: idFor([date, staffIndex, n, 'client']), name: clientName, avatar: '' },
        servicesBooked: chosen.map((sv) => ({
          _id: idFor([sv.name.replace(/\s+/g, '').toLowerCase()]),
          service: idFor([sv.name.replace(/\s+/g, '').toLowerCase()]),
          serviceName: sv.name,
          servicePrice: sv.price,
          serviceTime: sv.time,
          processingTime: 0,
          setupTime: 0,
          cleanupTime: 0,
          diaryColour: colours[sv.name],
        })),
        resources: bookedRoom ? [{ resourceId: bookedRoom._id, name: bookedRoom.name, groupId: ROOM_GROUP_ID }] : [],
        transaction: [],
      });

      cursor += length;
      // A gap only when there is still room for someone after it.
      if (rnd() < gapChance) {
        const gap = 15 * (1 + Math.floor(rnd() * maxGapSteps));
        if (until - (cursor + gap) >= shortest) cursor += gap;
      }
      n += 1;
    }
  });

  return { isWorking: true, rota: { startTime: start, endTime: end }, bookings, events, holidays: [] };
}

/**
 * The diary API response, made up. Same shape as `getDiaryData` returns, so
 * everything downstream (day view, week view, staff order) works unchanged.
 */
export function buildDemoDiary({ startDate, endDate, staffIds, override } = {}) {
  const { trade, palette, density } = { ...getDiaryDemo(), ...(override || {}) };
  const cfg = TRADES[trade] || TRADES.salon;
  const colours = serviceColours(trade in TRADES ? trade : 'salon', palette);

  const staff = cfg.staff.map((s, i) => {
    const [firstName, ...rest] = s.name.split(' ');
    return {
      id: idFor(['staff', i]),
      name: s.name,
      firstName,
      lastName: rest.join(' '),
      avatar: '',
      isSalonOwner: s.owner ? 1 : 0,
      bookingInterval: 15,
    };
  });
  const wanted = staffIds ? String(staffIds).split(',') : null;
  const included = wanted ? staff.filter((s) => wanted.indexOf(s.id) !== -1) : staff;

  const dates = {};
  const from = moment(startDate || moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');
  const to = moment(endDate || startDate || moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');
  for (let d = from.clone(); d.isSameOrBefore(to, 'day'); d.add(1, 'day')) {
    const date = d.format('YYYY-MM-DD');
    const byStaff = {};
    const usedNames = new Set();
    const rooms = demoResources(trade).units;
    const roomBusy = {};
    included.forEach((s) => {
      const index = staff.findIndex((x) => x.id === s.id);
      byStaff[s.id] = dayForStaff({ staff: cfg.staff[index], staffIndex: index, date, trade: trade in TRADES ? trade : 'salon', colours, density, usedNames, rooms, roomBusy });
    });
    dates[date] = byStaff;
  }

  return { staff: included, dates, staffOrder: included.map((s) => s.id), isDemo: true };
}

/** Rough totals for the settings page, so the demo can be described before it is switched on. */
export function demoSummary(trade, palette) {
  const cfg = TRADES[trade] || TRADES.salon;
  return {
    staff: cfg.staff.length,
    services: cfg.services.length,
    colours: paletteColours(palette).slice(0, 8),
  };
}
